import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useTheme } from '../../../contexts/ThemeContext';
import Firebase from 'firebase/compat/app'; // If not already imported for FieldValue
import { Timestamp } from 'firebase/firestore';
import { db, auth } from '@/js/external-js/config/main_config';
import { getPolicyList, savePolicy } from '@/services/policyService';

interface PolicyReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEvent?: { 
    title?: string; 
    description?: string; 
    id?: string; 
    policyNumber?: string; 
    policyOwner?: string; 
    policyStatus?: string; 
  };
  onDataChanged?: () => void;
}

interface PolicyReviewProps {
  policyId: string;
  companyID: string;
  reviewDate: Date;
  nextReviewDate: Date;
  reviewStatus: string;
  reviewNotes: string;
}


interface Policy {
  id: string;
  policy_version?: string;
  policy_status?: string;
  policy_owner?: string;
  policy_history?: any;
  policy_metadata?: any;
}

export const completePolicyReview = async ({
  policyId,
  companyID,
  reviewDate,
  nextReviewDate,
  reviewStatus,
  reviewNotes,
}: PolicyReviewProps): Promise<void> => {
  const policies = await getPolicyList(companyID);
  const currentPolicy = policies.find(p => p.id === policyId);

  if (!currentPolicy) throw new Error('Policy not found');

  const currentUser = auth.currentUser;
  const currentVersion = currentPolicy.policy_version || '1.0.0';

  const versionParts = currentVersion.split('.').map(Number);
  versionParts[2] = (versionParts[2] || 0) + 1;
  const newVersion = versionParts.join('.');

  const newReviewEntry = {
    previousVersion: currentVersion,
    reviewedAt: Timestamp.fromDate(reviewDate),
    reviewedBy: currentUser?.uid || '',
    reviewStatus,
    reviewNotes,
  };

  const updatedHistory = Array.isArray(currentPolicy.policy_history?.reviewHistory)
    ? [...currentPolicy.policy_history.reviewHistory, newReviewEntry]
    : [newReviewEntry];

  const updatedPolicy = {
    ...currentPolicy,
    policy_version: newVersion,
    policy_history: {
      ...currentPolicy.policy_history,
      last_reviewed: Timestamp.fromDate(reviewDate),
      reviewMetaData: {
        reviewedBy: currentUser?.uid || '',
        iso: reviewDate.toISOString(),
        unix: Math.floor(reviewDate.getTime() / 1000).toString(),
      },
      reviewHistory: updatedHistory,
      updateComments: reviewNotes,
      update_type: 'Policy Review',
      updated_date: Timestamp.fromDate(reviewDate),
    },
    policy_metadata: {
      ...currentPolicy.policy_metadata,
      lastEdited: Timestamp.fromDate(reviewDate),
      lastEditedBy: currentUser?.uid || '',
    },
  };

  const policyRef = db
    .collection('customers')
    .doc(companyID)
    .collection('product')
    .doc('policyPackages')
    .collection('policies')
    .doc(policyId);

  await policyRef.set(updatedPolicy);
};


export const usePolicyData = (companyID: string, policyId: string) => {
  const [policyData, setPolicyData] = useState<Policy | null>(null);

  useEffect(() => {
    if (!companyID || !policyId) return;
    const fetch = async () => {
      const list = await getPolicyList(companyID);
      const match = list.find((p) => p.id === policyId) || null;
      setPolicyData(match);
    };
    fetch();
  }, [companyID, policyId]);

  return policyData;
};


const PolicyReviewDialog: React.FC<PolicyReviewDialogProps> = ({ open, onOpenChange, selectedEvent, onDataChanged }) => {
  // Reset all dialog state when dialog opens or selectedEvent changes
  useEffect(() => {
    if (!open) return;
    setReviewStatus('approved');
    setReviewNotes('');
    setReviewDate(new Date());
    setNextReviewDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));
    // Add any other state resets as needed
  }, [open, selectedEvent]);
  const { isDark } = useTheme();
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewDate, setReviewDate] = useState<Date | undefined>(new Date());
  const policyData = usePolicyData(window.companyID, selectedEvent?.id || '');
  const [nextReviewDate, setNextReviewDate] = useState<Date | undefined>(new Date(new Date().setFullYear(new Date().getFullYear() + 1)));
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCompleteReview = async () => {
    if (!selectedEvent?.id || !window.companyID) return;
    setLoading(true);
    setError(null);
    try {
      if (!policyData) throw new Error('Policy data not loaded');
      // Build the update map (object) for the policy
      const updatedPolicy = {
        ...policyData,
        policy_status: reviewStatus,
        nextReviewDate: nextReviewDate,
        reviewNotes: reviewNotes,
        policy_history: {
          ...policyData.policy_history,
          last_reviewed: reviewDate,
          reviewNotes: reviewNotes,
          // Optionally add more history logic here if needed
        },
        // Add any other fields you want to update
      };
      await savePolicy(updatedPolicy, policyFile, window.companyID);
      if (typeof onDataChanged === 'function') onDataChanged();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to complete review');
      console.error('[PolicyReview] Error completing review:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[600px] ${isDark ? 'dark bg-background' : 'bg-white'}`}>
        <DialogHeader>
          <DialogTitle className="text-foreground">Policy Review</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {selectedEvent?.title || 'Review policy and update its status'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="policy-name" className="text-right text-foreground">
              Policy Name
            </Label>
            <Input
              id="policy-name"
              defaultValue={selectedEvent?.title || ''}
              className="col-span-3"
              readOnly
            />
          </div>
          
          {/* <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="policy-description" className="text-right text-foreground">
              Description
            </Label>
            <Textarea
              id="policy-description"
              defaultValue={selectedEvent?.description || ''}
              className="col-span-3"
              readOnly
              rows={3}
            />
          </div> */}
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="review-date" className="text-right text-foreground">
              Review Date
            </Label>
            <div className="col-span-3 w-full">
              <DatePicker 
                date={reviewDate} 
                setDate={setReviewDate}
                fullWidth
                enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="review-status" className="text-right text-foreground">
              Review Status
            </Label>
            <div className="col-span-3 w-full">
              <Select value={reviewStatus} onValueChange={setReviewStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="needs-revision">Needs Revision</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="next-review-date" className="text-right text-foreground">
              Next Review
            </Label>
            <div className="col-span-3 w-full">
              <DatePicker 
                date={nextReviewDate} 
                setDate={setNextReviewDate}
                fullWidth
                enableNaturalInput showPreview placeholder="Type 'tomorrow' or pick a date"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="review-notes" className="text-right text-foreground">
              Review Notes
            </Label>
            <Textarea
              id="review-notes"
              placeholder="Enter notes about this policy review..."
              className="col-span-3"
              rows={4}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="policy-id" className="col-span-1 text-sm font-medium text-gray-600 whitespace-normal break-words">ID</Label>
            <Input id="policy-id" value={selectedEvent?.id || ''} readOnly className="col-span-3 border border-gray-200 rounded-md" />
          </div>
          {/* We sorta show this ID somewhereelse no need to show it twice */}
          {/* <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="policy-number" className="col-span-1 text-sm font-medium text-gray-600 whitespace-normal break-words">Policy Number</Label>
            <Input id="policy-number" value={selectedEvent?.policyNumber || ''} readOnly className="col-span-3 border border-gray-200 rounded-md" />
          </div> */}
          {/* MAKE OWNER ACTUALLY SHOW UP OR HIDE IT.---  i hid it for now per Joe*/}
          {/* <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="policy-owner" className="col-span-1 text-sm font-medium text-gray-600 whitespace-normal break-words">Owner</Label>
            <Input id="policy-owner" value={policyData?.policy_owner || ''} readOnly className="col-span-3 border border-gray-200 rounded-md" />
          </div> */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="policy-status" className="col-span-1 text-sm font-medium text-gray-600 whitespace-normal break-words">Status</Label>
            <Input id="policy-status" value={policyData?.policy_status || ''} readOnly className="col-span-3 border border-gray-200 rounded-md" />
          </div>
        </div>
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleCompleteReview} disabled={loading}>
            {loading ? (<span className="flex items-center gap-2"><span className="spinner-border spinner-border-sm" role="status" /> Submitting...</span>) : 'Complete Review'}
          </Button>
        </DialogFooter>
        <div className="text-xs text-gray-400 mt-2 cursor-pointer" title="Edit Task">Policy ID: {selectedEvent?.componentId || selectedEvent?.id || 'N/A'}</div>
      </DialogContent>
    </Dialog>
  );
};

export default PolicyReviewDialog;
